import clsx from "clsx";
import { Tag } from "@/components/ui/tag";
import TextContent from "@/components/shared/content/TextContent";
import type { ReactNode } from "react";
import { TextFlip } from "@/components/shared/content/TextFlip";
import { useMemo } from "react";

export interface TextCombinationProps {
  label?: string;
  heading?: string;
  subHeading?: string;
  flipTextItems?: string[];
  flipInterval?: number;
  text?: string;
  richText?: ReactNode;
  textAlignment?: "left" | "center";
  headingSize?: "large" | "medium" | "default";
  layout?: "default" | "wide";
  textSize?: "small" | "large";
  maxWidth?: string;
  forceMainTitle?: boolean;
  twoLines?: boolean;
  className?: string;
}

export const GradientTextParser = ({ text }: { text: string }) => {
  return useMemo(() => {
    const cleanText = text;
    if (!cleanText) return null;

    const parts = cleanText.split(/(`[^`]*`)|(\*[^*]*\*)/g).filter(Boolean);

    return parts.map((part, index) => {
      if (part.startsWith("`") && part.endsWith("`")) {
        const inner = part.slice(1, -1);
        const isItalic = inner.startsWith("*") && inner.endsWith("*");
        const content = isItalic ? inner.slice(1, -1) : inner;
        return (
          <span
            key={index}
            className={`bg-[url('/images/footer_gradient-low-res.webp')] text-transparent bg-center bg-clip-text bg-cover bg-no-repeat${isItalic ? " italic" : ""}`}
          >
            {content}
          </span>
        );
      }
      if (part.startsWith("*") && part.endsWith("*")) {
        return <em key={index}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
  }, [text]);
};

const SubHeadingTextParser = ({
  text,
  sanitazeflipTextItems,
  flipInterval,
}: {
  text: string;
  sanitazeflipTextItems?: string[];
  flipInterval?: number;
}) => {
  return useMemo(() => {
    const cleanText = text;
    if (!cleanText) return null;

    const parts = cleanText.split(/(`[^`]*`)|(\[[^\]]*\])/g).filter(Boolean);

    return parts.map((part, index) => {
      if (part.startsWith("`") && part.endsWith("`")) {
        // Handle backtick content
        const content = part.slice(1, -1);
        return (
          <span
            key={"backtick-" + index}
            className="inline-block bg-[url('/images/footer_gradient-low-res.webp')] text-transparent bg-center bg-clip-text bg-cover bg-no-repeat"
          >
            {content}
          </span>
        );
      } else if (part.startsWith("[") && part.endsWith("]")) {
        // Handle bracket content
        const content = part.slice(1, -1);
        return (
          <TextFlip
            key={"bracket-" + index}
            initialWord={content}
            words={sanitazeflipTextItems || []}
            interval={flipInterval}
          />
        );
      }
      // Regular text
      return part;
    });
  }, [text, sanitazeflipTextItems, flipInterval]);
};


function TwoLineHeading({ heading, HeadingComponent, headingClass }: { heading: string; HeadingComponent: "h1" | "h2"; headingClass: string }) {
  const parts = (heading ?? heading).split("\n");
  const line1 = parts[0];
  const line2 = parts.slice(1).join("\n");
  return (
    <HeadingComponent className={headingClass}>
      {line1 && <span className="block"><GradientTextParser text={line1} /></span>}
      {line2 && <span className="block mb-4"><GradientTextParser text={line2} /></span>}
    </HeadingComponent>
  );
}

const TextCombination = ({
  label,
  heading,
  subHeading,
  flipTextItems,
  flipInterval,
  text,
  richText,
  textAlignment = "left",
  headingSize = "default",
  layout = "default",
  textSize = "small",
  maxWidth,
  forceMainTitle = false,
  twoLines = false,
  className,
}: TextCombinationProps) => {
  const sanitazeTextAlignment =
    (textAlignment as TextCombinationProps["textAlignment"]) ??
    "left";

  const sanitazeHeadingSize =
    (headingSize as TextCombinationProps["headingSize"]) ??
    "default";

  const sanitazeLayout =
    (layout as TextCombinationProps["layout"]) ?? "default";

  const sanitazeTextSize =
    (textSize as TextCombinationProps["textSize"]) ?? "small";

  const alignmentStyles = {
    left: "justify-start text-left",
    center: "justify-center text-center",
  };

  const headingSizeStyles = {
    large: "text-[32px] md:text-[36px] xl:text-[44px]",
    medium: "text-[36px] ",
    default: "text-[32px]",
  };

  const subHeadingSizeStyles = {
    large: "text-[24px] xl:text-[36px]",
    medium: "text-[24px]",
    default: "text-[26px]",
  };

  const HeadingComponent =
    sanitazeHeadingSize === "large" || forceMainTitle ? "h1" : "h2";
  const SubHeadingComponent =
    sanitazeHeadingSize === "large" || forceMainTitle ? "h2" : "h3";

  const sanitazeflipTextItems = useMemo(
    () => flipTextItems?.map((item) => item),
    [flipTextItems]
  );

  const sanitazeMaxWidth = maxWidth;

  const layoutStyles = {
    default: "gap-2 lg:max-w-[564px]",
    wide: "gap-6 max-w-[900px]",
  };

  const layoutGapStyles = {
    default: "gap-2",
    wide: "gap-6",
  };

  const textSizeStyles = {
    small: "text-base",
    large: "text-[26px]",
  };

  return (
    <div
      data-block="text-combinantion"
      className={clsx([
        "flex flex-col font-sans mx-auto",
        sanitazeMaxWidth
          ? [layoutGapStyles[sanitazeLayout], sanitazeMaxWidth]
          : [layoutStyles[sanitazeLayout], sanitazeHeadingSize === "large" && sanitazeLayout === "default" && "lg:max-w-[700px]!"],
        alignmentStyles[sanitazeTextAlignment],
        sanitazeHeadingSize === "default" && "pt-8 xl:pt-16",
        className,
      ])}
    >
      {label && (
        <div className="mb-6">
          <Tag variant="content">{label}</Tag>
        </div>
      )}
      <div>
        {heading && !twoLines && (
          <HeadingComponent
            className={clsx([
              "font-normal text-white m-0 leading-[130%] whitespace-pre-line",
              headingSizeStyles[sanitazeHeadingSize],
            ])}
          >
            <GradientTextParser text={heading} />
          </HeadingComponent>
        )}
        {heading && twoLines && <TwoLineHeading heading={heading} HeadingComponent={HeadingComponent} headingClass={clsx(["font-normal text-white m-0 leading-none", headingSizeStyles[sanitazeHeadingSize]])} />}
        {subHeading && (
          <SubHeadingComponent
            className={clsx([
              "text-white font-normal m-0 leading-[130%] whitespace-pre-line relative",
              subHeadingSizeStyles[sanitazeHeadingSize],
            ])}
          >
           <SubHeadingTextParser text={subHeading} sanitazeflipTextItems={sanitazeflipTextItems} flipInterval={flipInterval} />
          </SubHeadingComponent>
        )}
      </div>
      {text ? (
        <p
          className={clsx([
            textSizeStyles[sanitazeTextSize],
            "text-grey-550 leading-6 tracking-wide whitespace-pre-line",
            sanitazeHeadingSize === "large" && "mt-2",
          ])}
        >
          {text}
        </p>
      ) : (
        richText && (
          <div className={clsx([
            "[&>p]:text-grey-550 [&>p]:leading-6 [&>p]:tracking-wide [&>p>a]:text-white [&>p]:text-balance",
            sanitazeTextSize === "small" ? "[&>p]:text-base" : "[&>p]:text-[26px]",
          ])}>
            <TextContent>{richText}</TextContent>
          </div>
        )
      )}
    </div>
  );
};

export default TextCombination;
